import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { LogOut, Inbox, Trash2, Check, MailOpen, Mail, Loader2, AlertCircle } from "lucide-react";
import SEO from "@/components/SEO";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "othryx.admin.token";

export default function Admin() {
    const { t } = useLang();
    const a = t.admin;
    const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
    const [authed, setAuthed] = useState(false);
    const [loginError, setLoginError] = useState("");
    const [pending, setPending] = useState(false);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(null);

    const load = useCallback(async (tk) => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/contact`, { headers: { "X-Admin-Token": tk } });
            setItems(res.data || []);
        } catch (e) {
            console.error(e);
            if (e?.response?.status === 401) logout();
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (token) {
            (async () => {
                try {
                    await axios.post(`${API}/admin/verify`, {}, { headers: { "X-Admin-Token": token } });
                    setAuthed(true);
                    load(token);
                } catch {
                    sessionStorage.removeItem(TOKEN_KEY);
                    setToken("");
                    setAuthed(false);
                }
            })();
        }
    }, [load, token]);

    const submitLogin = async (e) => {
        e.preventDefault();
        setPending(true);
        setLoginError("");
        try {
            const tk = e.target.token.value.trim();
            await axios.post(`${API}/admin/verify`, {}, { headers: { "X-Admin-Token": tk } });
            sessionStorage.setItem(TOKEN_KEY, tk);
            setToken(tk);
            setAuthed(true);
            load(tk);
        } catch {
            setLoginError(a.failed);
        } finally {
            setPending(false);
        }
    };

    const logout = () => {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
        setAuthed(false);
        setItems([]);
        setSelected(null);
    };

    const toggleRead = async (item) => {
        try {
            const res = await axios.patch(
                `${API}/contact/${item.id}?read=${!item.read}`,
                {},
                { headers: { "X-Admin-Token": token } }
            );
            setItems((arr) => arr.map((x) => (x.id === item.id ? res.data : x)));
            if (selected?.id === item.id) setSelected(res.data);
        } catch (e) {
            console.error(e);
        }
    };

    const del = async (item) => {
        if (!window.confirm(a.confirmDelete)) return;
        try {
            await axios.delete(`${API}/contact/${item.id}`, { headers: { "X-Admin-Token": token } });
            setItems((arr) => arr.filter((x) => x.id !== item.id));
            if (selected?.id === item.id) setSelected(null);
        } catch (e) {
            console.error(e);
        }
    };

    const newCount = items.filter((i) => !i.read).length;

    if (!authed) {
        return (
            <main className="min-h-screen pt-28 lg:pt-36 pb-20 blueprint-bg" data-testid="page-admin-login">
                <SEO pathKey="admin" path="/admin" />
                <div className="mx-auto max-w-md px-6">
                    <div className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-steel-600">
                        OTHRYX · Admin
                    </div>
                    <h1 className="font-display mt-5 text-4xl uppercase tracking-tighter text-navy leading-[0.95]">
                        {a.login}
                    </h1>
                    <form onSubmit={submitLogin} className="mt-10 bg-white border border-steel-100 p-6 space-y-5">
                        <label className="block">
                            <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-steel-600">
                                {a.tokenLabel}
                            </span>
                            <input
                                type="password"
                                name="token"
                                required
                                placeholder={a.tokenPh}
                                data-testid="admin-token-input"
                                className="mt-2 block w-full bg-white border border-steel-200 focus:border-navy focus:outline-none px-4 py-3 text-navy text-sm transition-colors"
                            />
                        </label>
                        {loginError && (
                            <div data-testid="admin-login-error" className="font-mono-tech text-[11px] uppercase tracking-wider text-signal inline-flex items-center gap-2">
                                <AlertCircle className="h-4 w-4" /> {loginError}
                            </div>
                        )}
                        <button
                            type="submit"
                            disabled={pending}
                            data-testid="admin-login-submit"
                            className="inline-flex items-center gap-3 bg-navy text-white px-6 py-3 font-mono-tech text-[12px] uppercase tracking-[0.2em] hover:bg-navy-light transition-colors disabled:opacity-60"
                        >
                            {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                            {a.enter}
                        </button>
                    </form>
                </div>
            </main>
        );
    }

    return (
        <main className="min-h-screen pt-28 lg:pt-32 pb-20" data-testid="page-admin">
            <SEO pathKey="admin" path="/admin" />
            <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <div className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-steel-600">
                            OTHRYX · {a.title}
                        </div>
                        <h1 className="font-display mt-3 text-4xl lg:text-5xl uppercase tracking-tighter text-navy leading-[0.95]">
                            <Inbox className="inline-block h-8 w-8 me-3 align-middle" />
                            {a.title}
                        </h1>
                    </div>
                    <div className="flex items-center gap-5">
                        <div className="font-mono-tech text-[11px] uppercase tracking-wider text-steel-600">
                            {a.total}: <span className="text-navy">{items.length}</span> · {a.new}: <span className="text-signal">{newCount}</span>
                        </div>
                        <button
                            onClick={logout}
                            data-testid="admin-logout"
                            className="inline-flex items-center gap-2 border border-navy text-navy px-4 py-2 font-mono-tech text-[11px] uppercase tracking-[0.2em] hover:bg-navy hover:text-white transition-colors"
                        >
                            <LogOut className="h-4 w-4" /> {a.logout}
                        </button>
                    </div>
                </div>

                <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-5 border border-steel-100 bg-white max-h-[70vh] overflow-y-auto" data-testid="admin-list">
                        {loading && <div className="p-6 text-sm text-steel-600">Loading…</div>}
                        {!loading && items.length === 0 && (
                            <div className="p-8 text-center text-steel-600 font-mono-tech text-xs uppercase tracking-wider" data-testid="admin-empty">
                                {a.empty}
                            </div>
                        )}
                        {items.map((it) => (
                            <button
                                key={it.id}
                                onClick={() => setSelected(it)}
                                data-testid={`admin-row-${it.id}`}
                                className={`group block w-full text-start p-5 border-b border-steel-100 transition-colors ${
                                    selected?.id === it.id ? "bg-steel-50" : "hover:bg-steel-50"
                                }`}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            {!it.read && (
                                                <span className="inline-block w-2 h-2 bg-signal" aria-label="unread" />
                                            )}
                                            <span className="font-display text-lg tracking-tight text-navy truncate">
                                                {it.name}
                                            </span>
                                            <span className="font-mono-tech text-[10px] uppercase tracking-wider text-steel-400">
                                                · {it.locale || "en"}
                                            </span>
                                        </div>
                                        <div className="text-xs text-steel-600 truncate">{it.email}{it.company ? ` · ${it.company}` : ""}</div>
                                        <div className="mt-2 text-sm text-steel-800 line-clamp-2">{it.subject || it.message}</div>
                                    </div>
                                    <div className="font-mono-tech text-[10px] uppercase tracking-wider text-steel-400 whitespace-nowrap">
                                        {new Date(it.created_at).toLocaleDateString(undefined, { day: "2-digit", month: "short" })}
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>

                    <div className="lg:col-span-7" data-testid="admin-detail">
                        {!selected && (
                            <div className="border border-dashed border-steel-200 p-10 text-center text-steel-500 font-mono-tech text-xs uppercase tracking-wider">
                                {a.details}
                            </div>
                        )}
                        {selected && (
                            <article className="border border-steel-100 bg-white p-6 lg:p-8 space-y-5">
                                <header className="flex flex-wrap items-start justify-between gap-4 border-b border-steel-100 pb-5">
                                    <div>
                                        <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-steel-600">
                                            {a.sentBy}
                                        </div>
                                        <h2 className="font-display text-2xl lg:text-3xl tracking-tight text-navy mt-1">
                                            {selected.name}
                                        </h2>
                                        <a
                                            href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject || "OTHRYX inquiry")}`}
                                            data-testid="admin-reply-email"
                                            className="block mt-1 text-sm text-signal link-underline"
                                        >
                                            {selected.email}
                                        </a>
                                        {selected.company && (
                                            <div className="text-xs text-steel-600 mt-1">{selected.company}</div>
                                        )}
                                        {selected.phone && (
                                            <div className="text-xs text-steel-600">{selected.phone}</div>
                                        )}
                                    </div>
                                    <div className="text-end font-mono-tech text-[10px] uppercase tracking-wider text-steel-500">
                                        <div>{a.sentAt}</div>
                                        <div className="text-navy mt-1">{new Date(selected.created_at).toLocaleString()}</div>
                                        <div className="mt-1">{a.from}: <span className="text-navy">{selected.locale || "en"}</span></div>
                                    </div>
                                </header>

                                {selected.subject && (
                                    <div>
                                        <div className="font-mono-tech text-[10px] uppercase tracking-wider text-steel-600">Subject</div>
                                        <div className="font-display text-lg text-navy mt-1">{selected.subject}</div>
                                    </div>
                                )}
                                <div>
                                    <div className="font-mono-tech text-[10px] uppercase tracking-wider text-steel-600">Message</div>
                                    <p className="mt-2 text-steel-800 whitespace-pre-line leading-relaxed text-sm">{selected.message}</p>
                                </div>

                                <div className="flex flex-wrap gap-2 pt-2 border-t border-steel-100">
                                    <button
                                        onClick={() => toggleRead(selected)}
                                        data-testid="admin-toggle-read"
                                        className="inline-flex items-center gap-2 border border-navy text-navy px-4 py-2 font-mono-tech text-[11px] uppercase tracking-[0.2em] hover:bg-navy hover:text-white transition-colors"
                                    >
                                        {selected.read ? <Mail className="h-4 w-4" /> : <MailOpen className="h-4 w-4" />}
                                        {selected.read ? a.markUnread : a.markRead}
                                    </button>
                                    <a
                                        href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject || "OTHRYX inquiry")}`}
                                        className="inline-flex items-center gap-2 bg-navy text-white px-4 py-2 font-mono-tech text-[11px] uppercase tracking-[0.2em] hover:bg-navy-light transition-colors"
                                    >
                                        <Check className="h-4 w-4" /> {a.replyEmail}
                                    </a>
                                    <button
                                        onClick={() => del(selected)}
                                        data-testid="admin-delete"
                                        className="ms-auto inline-flex items-center gap-2 text-signal hover:text-white hover:bg-signal border border-signal/40 px-4 py-2 font-mono-tech text-[11px] uppercase tracking-[0.2em] transition-colors"
                                    >
                                        <Trash2 className="h-4 w-4" /> {a.delete}
                                    </button>
                                </div>
                            </article>
                        )}
                    </div>
                </div>
            </div>
        </main>
    );
}
